import { ChangeEvent, Fragment, useCallback, useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { useMsal } from "@azure/msal-react";
import { InputNumber } from "../components/Common/InputNumber";
import { apiAccessRequest } from "../constants";
import { useAlertContext, useUserContext } from "../contexts";
import { AlertType } from "../models";

type VersionParts = [number, number, number];

const initialVersion: VersionParts = [0, 0, 0];

const versionsMatch = (first: VersionParts, second: VersionParts) =>
	first.every((value, index) => value === second[index]);

const concatenateVersion = (version: VersionParts) => version.join(".");

const splitVersion = (version: string): VersionParts => {
	const value = version.trim();

	if (!/^\d+\.\d+\.\d+$/.test(value)) {
		throw new Error(`Invalid app version: ${version}`);
	}

	return value.split(".").map(Number) as VersionParts;
};

export const AppVersions = () => {
	const { showAlert } = useAlertContext();
	const { account } = useUserContext();
	const { instance } = useMsal();
	const [ourMedicalVersion, setOurMedicalVersion] =
		useState<VersionParts>(initialVersion);
	const [ourSageVersion, setOurSageVersion] =
		useState<VersionParts>(initialVersion);
	const [savedOurMedicalVersion, setSavedOurMedicalVersion] =
		useState<VersionParts>(initialVersion);
	const [savedOurSageVersion, setSavedOurSageVersion] =
		useState<VersionParts>(initialVersion);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);

	const getVersion = useCallback(
		async (application: "OurMedical" | "OurSage", headers: HeadersInit) => {
			const response = await fetch(
				`${process.env.REACT_APP_CONSULTS_URL}appversion/${application}`,
				{ method: "GET", headers }
			);

			if (!response.ok) {
				throw new Error(`${application} app version could not be retrieved`);
			}

			const responseText = await response.text();
			return splitVersion(String(responseText));
		},
		[]
	);

	const loadVersions = useCallback(async () => {
		try {
			setIsLoading(true);
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				Authorization: `Bearer ${tokenResponse.accessToken}`
			};
			const [medicalVersion, sageVersion] = await Promise.all([
				getVersion("OurMedical", headers),
				getVersion("OurSage", headers)
			]);
			setOurMedicalVersion(medicalVersion);
			setSavedOurMedicalVersion(medicalVersion);
			setOurSageVersion(sageVersion);
			setSavedOurSageVersion(sageVersion);
		} catch (error) {
			console.info(error);
			showAlert({
				message: "App versions could not be retrieved",
				alertType: AlertType.error
			});
		} finally {
			setIsLoading(false);
		}
	}, [account, getVersion, instance, showAlert]);

	useEffect(() => {
		loadVersions();
	}, [loadVersions]);

	const updateVersion =
		(
			setVersion: React.Dispatch<React.SetStateAction<VersionParts>>,
			index: number,
			maximum?: number
		) =>
		async (event: ChangeEvent<HTMLInputElement>) => {
			const value = Number(event.target.value);
			const boundedValue = Math.max(0, Math.min(maximum ?? value, value));

			setVersion((current) => {
				const updated = [...current] as VersionParts;
				updated[index] = boundedValue;
				return updated;
			});
		};

	const postVersion = async (
		application: "OurMedical" | "OurSage",
		version: VersionParts,
		headers: HeadersInit
	) => {
		const response = await fetch(
			`${process.env.REACT_APP_CONSULTS_URL}appversion/${application}`,
			{
				method: "POST",
				headers,
				body: JSON.stringify(concatenateVersion(version))
			}
		);

		if (!response.ok) {
			throw new Error(`${application} app version could not be saved`);
		}
	};

	const handleSave = async () => {
		const medicalChanged = !versionsMatch(
			ourMedicalVersion,
			savedOurMedicalVersion
		);
		const sageChanged = !versionsMatch(ourSageVersion, savedOurSageVersion);

		if (!medicalChanged && !sageChanged) return;

		try {
			setIsSaving(true);
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				Authorization: `Bearer ${tokenResponse.accessToken}`,
				"Content-Type": "application/json"
			};

			await Promise.all([
				...(medicalChanged
					? [
							postVersion("OurMedical", ourMedicalVersion, headers).then(() => {
								setSavedOurMedicalVersion(ourMedicalVersion);
								setOurMedicalVersion(ourMedicalVersion);
							})
						]
					: []),
				...(sageChanged
					? [
							postVersion("OurSage", ourSageVersion, headers).then(() => {
								setSavedOurSageVersion(ourSageVersion);
								setOurSageVersion(ourSageVersion);
							})
						]
					: [])
			]);

			showAlert({
				message: "App versions were saved",
				alertType: AlertType.success,
				timeout: true
			});
		} catch (error) {
			console.info(error);
			showAlert({
				message: "App versions could not be saved",
				alertType: AlertType.error
			});
		} finally {
			setIsSaving(false);
		}
	};

	const handleCancel = async () => {
		const medicalChanged = !versionsMatch(
			ourMedicalVersion,
			savedOurMedicalVersion
		);
		const sageChanged = !versionsMatch(ourSageVersion, savedOurSageVersion);
		if (!medicalChanged && !sageChanged) {
			return;
		}

		if (medicalChanged) {
			setOurMedicalVersion(savedOurMedicalVersion);
		}
		if (sageChanged) {
			setOurSageVersion(savedOurSageVersion);
		}
		showAlert({
			message: "Changes cancelled",
			alertType: AlertType.info,
			timeout: true
		});
	};

	const renderVersionInputs = (
		title: string,
		version: VersionParts,
		setVersion: React.Dispatch<React.SetStateAction<VersionParts>>
	) => (
		<section className="flex flex-col gap-4">
			<h2 className="ch-l-text">{title}</h2>
			<div className="flex gap-4">
				{version.map((value, index) => (
					<Fragment key={`${title}-${index}`}>
						<InputNumber
							name={`${title}-${index}`}
							value={value}
							min={0}
							width={80}
							handleChange={updateVersion(setVersion, index)}
						/>
						{index < 2 && <div>.</div>}
					</Fragment>
				))}
			</div>
		</section>
	);

	if (isLoading) {
		return (
			<div className="flex justify-center p-4">
				<CircularProgress size={70} />
			</div>
		);
	}

	return (
		<div className="flex flex-col p-4 gap-8 ch-lightestgrey-background">
			{renderVersionInputs(
				"Our Medical App Version",
				ourMedicalVersion,
				setOurMedicalVersion
			)}
			{renderVersionInputs(
				"Our Sage App Version",
				ourSageVersion,
				setOurSageVersion
			)}
			<div className="flex gap-4">
				<button
					className="ch-button ch-button-primary justify-center"
					disabled={isSaving}
					onClick={handleCancel}
				>
					{isSaving ? <CircularProgress size={20} /> : "Cancel"}
				</button>
				<button
					className="ch-button ch-button-primary justify-center"
					disabled={isSaving}
					onClick={handleSave}
				>
					{isSaving ? <CircularProgress size={20} /> : "Save"}
				</button>
			</div>
		</div>
	);
};
